import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth.service';
import DPSLogo from '../common/DPSLogo';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Set when the same email has profiles at more than one school: the
  // password has been verified and the gateway asks which school to enter.
  const [schools, setSchools] = useState(null);
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (token) {
      if (user.role === 'admin') {
        navigate('/dashboard');
      } else {
        navigate('/dashboard');
      }
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const signIn = async (tenant) => {
    try {
      setLoading(true);
      setError('');
      const response = await authService.login(formData.email, formData.password, tenant);

      // Two schools know this person — show the choice; the password is
      // already proven, so picking one signs straight in.
      if (response.data.schoolChoiceRequired) {
        setSchools(response.data.schools || []);
        return;
      }

      if (response.data.user.role === 'admin') {
        navigate('/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setSchools(null);
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await signIn();
  };

  return (
    <div className="min-h-screen bg-dps-wash flex items-center justify-center">
      <div className="bg-white p-8 rounded-3xl shadow-dps-lg border border-dps-hairline w-[480px] mx-4">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <DPSLogo size="full" />
          </div>
          <div className="flex items-center justify-center gap-2 text-base text-gray-500 mt-2">
            <span className="text-orange-500 font-medium">Welcome back,</span>
            <span>please sign in</span>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {schools ? (
          /* The same account exists at more than one school. The password is
             already verified — choosing a school signs straight in. */
          <div className="space-y-4">
            <p className="text-sm text-gray-600 text-center">
              Your account belongs to more than one school. Which one are you signing in to?
            </p>
            <div className="space-y-3">
              {schools.map((s) => (
                <button
                  key={s.slug}
                  type="button"
                  disabled={loading}
                  onClick={() => signIn(s.slug)}
                  className="w-full text-left px-4 py-3.5 rounded-xl border border-dps-hairline hover:border-orange-400 hover:bg-orange-50 transition font-medium text-gray-800 disabled:opacity-50"
                >
                  {s.name}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setSchools(null)}
              className="w-full text-sm text-gray-400 hover:text-gray-600"
            >
              ← Back
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
              placeholder="Enter your email"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
              placeholder="Enter your password"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-dps w-full justify-center py-3"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Signing In...
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>
        )}

        {/* No self-registration. Accounts for the whole estate are created by
            an administrator in the Auth Gateway, and the same login works for
            employees, payments and platform admin. This link used to point at
            an open, unauthenticated endpoint: anyone with a work-looking email
            address could give themselves an account on the school's hiring
            system. */}
        <div className="mt-6 text-center text-sm text-gray-600">
          Need access? Ask your administrator to create your account.
        </div>
      </div>
    </div>
  );
};

export default Login;